import { API_BASE, authHeaders } from './apiAuth'

/**
 * Bria's Background and Eraser models on the board (Huanrui, 2026-10-27),
 * through fal by way of the Worker (workers/api/src/routes/bria.ts).
 *
 * The board sends a copy of the picture (and, to erase, a mask: white where
 * to erase); what the model makes is kept as a project file as it came, and
 * the board then puts the finished picture together with the original at
 * full size (ProjectCanvas, runBackground / runEraser).
 */
// Background Replace was here for a day and taken out (Huanrui, 2026-10-27).
export type BriaOp = 'remove' | 'erase'

/** What each costs, in credits (1,000 = $1), by fal's pages on 2026-10-27. */
export const BRIA_CREDITS: Record<BriaOp, number> = { remove: 18, erase: 40 }

/** What each model is called on the corner button (a picture made by Replace, while it was there, too). */
export const BRIA_MODEL_LABEL: Record<string, string> = {
  remove: 'Bria RMBG 2.0',
  replace: 'Bria Background Replace',
  erase: 'Bria Eraser',
}

async function reason(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string }
    if (body?.error) return body.error
  } catch {
    /* not JSON */
  }
  return `HTTP ${response.status}`
}

async function base64Of(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer())
  let text = ''
  for (let i = 0; i < bytes.length; i += 0x8000) text += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  return btoa(text)
}

export interface BriaAsk {
  op: BriaOp
  projectId: string
  picture: Blob
  /** Erase only: a PNG the picture's size, white where to erase. */
  mask?: Blob
  /** The picture has see-through parts worth keeping (erase). */
  png?: boolean
  from: string
  title: string
}

/** Answers the model's picture: its project file id and size. */
export async function runBria(ask: BriaAsk): Promise<{ fileId: string; width: number; height: number }> {
  const project = encodeURIComponent(ask.projectId)
  const started = await fetch(`${API_BASE}/api/canvas-bria?project=${project}&op=${ask.op}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({
      image: await base64Of(ask.picture),
      ...(ask.mask ? { mask: await base64Of(ask.mask) } : {}),
      ...(ask.png ? { png: true } : {}),
    }),
  })
  if (!started.ok) throw new Error(await reason(started))
  const { task } = (await started.json()) as { task?: string }
  if (!task) throw new Error('fal did not take it')

  const startedAt = Date.now()
  let misses = 0
  for (;;) {
    await new Promise((wake) => setTimeout(wake, 2000))
    if (Date.now() - startedAt > 5 * 60 * 1000) throw new Error('No answer from fal after five minutes')
    try {
      const asked = await fetch(
        `${API_BASE}/api/canvas-bria?project=${project}&op=${ask.op}&task=${encodeURIComponent(task)}` +
          `&from=${encodeURIComponent(ask.from)}&title=${encodeURIComponent(ask.title)}`,
        { headers: await authHeaders() },
      )
      if (!asked.ok) throw new Error(await reason(asked))
      const body = (await asked.json()) as { state?: string; fileId?: string; width?: number; height?: number; error?: string }
      misses = 0
      if (body.state === 'done' && body.fileId) {
        console.info(`[WonderWorks] ${ask.op}: fal answered in ${Math.round((Date.now() - startedAt) / 1000)} s`)
        return { fileId: body.fileId, width: body.width ?? 0, height: body.height ?? 0 }
      }
      if (body.state === 'failed') throw Object.assign(new Error(body.error || 'It did not work'), { final: true })
    } catch (error) {
      if ((error as { final?: boolean }).final) throw error
      misses += 1
      if (misses >= 5) throw error
    }
  }
}
